import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Availability, ProfessionalData } from '../../../../models';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { getWeekDayTranslationKey } from '../../../../shared/utils';

interface AvailabilityCalendarProps {
    availability: Availability[];
    professionals: ProfessionalData[];
    selectedSlot: Availability | undefined;
    onSelectSlot: (slot: Availability) => void;
}

// Single scroll container: CalendarContainer owns both horizontal and vertical scrolling.
// DayHeadersRow sticks at top:0 relative to this container, so horizontal scroll
// moves both header and content cells together — perfect alignment, no JS sync needed.
const CalendarContainer = styled.div`
    max-height: 55vh;
    overflow: auto;
`;

const DayHeadersRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    padding-bottom: var(--spacer-XS);
    margin-bottom: var(--spacer-XS);
    min-width: max-content;
`;

const DayHeaderCell = styled.div`
    width: 130px;
    flex-shrink: 0;
    font-weight: bold;
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: center;
`;

const ContentRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
    align-items: flex-start;
    padding-bottom: var(--spacer-XS);
`;

const DayColumn = styled.div`
    width: 130px;
    flex-shrink: 0;
`;

const SlotChip = styled.button<{ $selected: boolean }>`
    display: block;
    width: 100%;
    padding: var(--spacer-XS) var(--spacer-S);
    margin-bottom: var(--spacer-XS);
    border: var(--border-thin) solid ${({ $selected }) => $selected ? 'var(--appGreen)' : 'var(--appLigthGrey)'};
    border-radius: 4px;
    background: ${({ $selected }) => $selected ? 'var(--success-bg)' : 'white'};
    cursor: pointer;
    text-align: left;
    font-size: 0.85rem;

    &:hover {
        border-color: var(--appGreen);
        background: var(--bg-hover-light);
    }
`;

const SlotTime = styled.span`
    display: block;
    font-weight: bold;
    color: var(--text-body);
`;

const SlotDetail = styled.span`
    display: block;
    color: var(--text-muted);
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
`;

const isSelected = (a: Availability, b: Availability | undefined): boolean =>
    !!b && a.dateTime === b.dateTime && a.professionalId === b.professionalId && a.room === b.room;

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
    availability,
    professionals,
    selectedSlot,
    onSelectSlot,
}) => {
    const t = useTranslations();

    const professionalMap = useMemo(
        () => new Map(professionals.map(p => [p.id, p])),
        [professionals],
    );

    const dayGroups = useMemo(() => {
        const map = new Map<string, Availability[]>();
        availability.forEach(slot => {
            const date = moment(slot.dateTime).format('YYYY-MM-DD');
            if (!map.has(date)) map.set(date, []);
            map.get(date)!.push(slot);
        });
        map.forEach(slots => slots.sort((a, b) => a.dateTime.localeCompare(b.dateTime)));
        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, slots]) => ({ date, slots }));
    }, [availability]);

    return (
        <CalendarContainer>
            <DayHeadersRow>
                {dayGroups.map(({ date }) => (
                    <DayHeaderCell key={date}>
                        {t(getWeekDayTranslationKey(new Date(date).getDay())).substring(0, 3)} {moment(date).format('DD/MM')}
                    </DayHeaderCell>
                ))}
            </DayHeadersRow>
            <ContentRow>
                {dayGroups.map(({ date, slots }) => (
                    <DayColumn key={date}>
                        {slots.map((slot, i) => {
                            const prof = professionalMap.get(slot.professionalId);
                            return (
                                <SlotChip
                                    key={i}
                                    $selected={isSelected(slot, selectedSlot)}
                                    onClick={() => onSelectSlot(slot)}
                                >
                                    <SlotTime>{moment(slot.dateTime).format('HH:mm')}</SlotTime>
                                    {prof && <SlotDetail>{prof.name}</SlotDetail>}
                                    {slot.room && <SlotDetail>{slot.room}</SlotDetail>}
                                </SlotChip>
                            );
                        })}
                    </DayColumn>
                ))}
            </ContentRow>
        </CalendarContainer>
    );
};

export default AvailabilityCalendar;
